import { useEffect } from "react";
import { create } from "zustand";
import type { Activity, CustomStatus, Me, MyPresence, Presence, PresenceBatch, PresenceEvent, PresenceState, PresenceStatusChoice } from "@atrium/shared";
import { api, errorMessage } from "./api";
import { getSocket } from "./socket";
import { queryClient } from "./queryClient";
import { toast } from "../components/ui/Toast";

/*
 * Live presence for any user, keyed by id. Fed by the socket "presence" event
 * (RealtimeBridge) and by batched GET /api/presence reads: components call
 * usePresence(id); unknown or stale ids are collected and fetched together on the
 * next animation frame, at most 100 per request.
 */

export interface PresenceEntry {
  status: PresenceState;
  customStatus: CustomStatus | null;
  activity: Activity | null;
  lastSeenAt: string | null;
  activeCommunity: { id: string; name: string } | null;
  /** When this entry was last confirmed (fetch or live event). */
  at: number;
}

interface PresenceStore {
  byId: Record<string, PresenceEntry>;
  upsert: (entries: Record<string, PresenceEntry>) => void;
}

export const usePresenceStore = create<PresenceStore>((set) => ({
  byId: {},
  upsert: (entries) => set((s) => ({ byId: { ...s.byId, ...entries } })),
}));

/** Entries older than this are re-fetched when next read (events only reach shared spaces). */
const STALE_MS = 90_000;

const fromPresence = (p: Presence): PresenceEntry => ({
  status: p.status,
  customStatus: p.customStatus,
  activity: p.activity,
  lastSeenAt: p.lastSeenAt,
  activeCommunity: p.activeCommunity,
  at: Date.now(),
});

/** Called by RealtimeBridge for every "presence" event. */
export function applyPresenceEvent(e: PresenceEvent) {
  const prev = usePresenceStore.getState().byId[e.userId];
  usePresenceStore.getState().upsert({
    [e.userId]: { status: e.status, customStatus: e.customStatus, activity: e.activity, lastSeenAt: e.lastSeenAt, activeCommunity: prev?.activeCommunity ?? null, at: Date.now() },
  });
}

/* ───────────── Batched fetching ───────────── */

const queued = new Set<string>();
const inFlight = new Set<string>();
let frame = 0;

function flush() {
  frame = 0;
  const ids = [...queued].filter((id) => !inFlight.has(id));
  queued.clear();
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    chunk.forEach((id) => inFlight.add(id));
    api
      .get<PresenceBatch>(`/api/presence?ids=${chunk.join(",")}`)
      .then((r) => {
        const entries: Record<string, PresenceEntry> = {};
        for (const p of r.items) entries[p.userId] = fromPresence(p);
        // Unknown ids (deleted accounts) read as offline so they aren't re-requested in a loop.
        for (const id of chunk) entries[id] ??= { status: "offline", customStatus: null, activity: null, lastSeenAt: null, activeCommunity: null, at: Date.now() };
        usePresenceStore.getState().upsert(entries);
      })
      .catch(() => {
        /* transient; the next read retries */
      })
      .finally(() => chunk.forEach((id) => inFlight.delete(id)));
  }
}

/** Ask for fresh presence for these users (batched per animation frame). */
export function requestPresence(ids: string[]) {
  const now = Date.now();
  const byId = usePresenceStore.getState().byId;
  for (const id of ids) {
    const e = byId[id];
    if (!e || now - e.at > STALE_MS) queued.add(id);
  }
  if (queued.size && !frame) frame = requestAnimationFrame(flush);
}

/** Live presence for one user; fetched lazily on first use. */
export function usePresence(userId: string | null | undefined): PresenceEntry | undefined {
  const entry = usePresenceStore((s) => (userId ? s.byId[userId] : undefined));
  useEffect(() => {
    if (userId) requestPresence([userId]);
  }, [userId, entry?.at]);
  return entry;
}

/** Prefetch presence for a list (member lists, DM lists). */
export function usePresenceList(userIds: string[]) {
  const key = userIds.join(",");
  useEffect(() => {
    if (key) requestPresence(key.split(","));
  }, [key]);
}

/* ───────────── Labels ───────────── */

export const STATUS_LABELS: Record<PresenceState | PresenceStatusChoice, string> = {
  online: "Online",
  idle: "Idle",
  dnd: "Do not disturb",
  offline: "Offline",
  invisible: "Invisible",
};

const ACTIVITY_VERBS: Record<Activity["type"], string> = { playing: "Playing", listening: "Listening to", watching: "Watching", working: "Working on", custom: "" };

/** "Playing Chess", "Listening to Lo-fi", or the custom text. */
export function activityLabel(a: Activity): string {
  const verb = ACTIVITY_VERBS[a.type];
  return verb ? `${verb} ${a.name}` : a.name;
}

/* ───────────── Your own presence ───────────── */

function patchMe(p: MyPresence) {
  const me = queryClient.getQueryData<Me | null>(["me"]);
  if (me) queryClient.setQueryData<Me>(["me"], { ...me, presence: p });
  const own = me?.id;
  if (own) {
    const prev = usePresenceStore.getState().byId[own];
    usePresenceStore.getState().upsert({
      [own]: {
        status: p.status === "invisible" ? "offline" : p.status === "online" && prev?.status === "idle" ? "idle" : p.status,
        customStatus: p.customStatus,
        activity: p.activity,
        lastSeenAt: null,
        activeCommunity: prev?.activeCommunity ?? null,
        at: Date.now(),
      },
    });
  }
}

async function run(req: Promise<MyPresence>, fail: string) {
  try {
    const p = await req;
    patchMe(p);
    return p;
  } catch (e) {
    toast.error(errorMessage(e, fail));
    return null;
  }
}

export const presenceActions = {
  setStatus: (status: PresenceStatusChoice) => run(api.put<MyPresence>("/api/presence/status", { status }), "Couldn't change your status"),
  setCustomStatus: (s: { text: string; emoji: string | null; expiresIn: "30m" | "1h" | "4h" | "today" | "never" }) =>
    run(api.put<MyPresence>("/api/presence/custom-status", { ...s, tzOffset: new Date().getTimezoneOffset() }), "Couldn't set your status"),
  clearCustomStatus: () => run(api.del<MyPresence>("/api/presence/custom-status"), "Couldn't clear your status"),
  setActivity: (a: { type: Activity["type"]; name: string; details?: string; expiresIn: "30m" | "1h" | "4h" | "today" | "never" }) =>
    run(api.put<MyPresence>("/api/presence/activity", { ...a, tzOffset: new Date().getTimezoneOffset() }), "Couldn't set your activity"),
  clearActivity: () => run(api.del<MyPresence>("/api/presence/activity"), "Couldn't clear your activity"),
};

/* ───────────── Idle and focus reporting ───────────── */

const IDLE_AFTER_MS = 5 * 60_000;
const HIDDEN_IDLE_AFTER_MS = 10 * 60_000;

/**
 * Reports this device as idle after 5 minutes without pointer/keyboard input, or
 * 10 minutes with the tab hidden; any input brings it back. Also reports which
 * community the URL points at. Both are re-sent after a reconnect.
 */
export function usePresenceReporter(communityId: string | null) {
  useEffect(() => {
    let idle = false;
    let inputTimer: ReturnType<typeof setTimeout> | undefined;
    let hiddenTimer: ReturnType<typeof setTimeout> | undefined;
    let lastInput = 0;
    const emit = (v: boolean) => {
      if (idle === v) return;
      idle = v;
      getSocket()?.emit("presence:idle", { idle: v });
    };
    const armInput = () => {
      clearTimeout(inputTimer);
      inputTimer = setTimeout(() => emit(true), IDLE_AFTER_MS);
    };
    const onInput = () => {
      const now = Date.now();
      if (now - lastInput < 1000 && !idle) return; // throttle busy pointer streams
      lastInput = now;
      if (document.visibilityState === "visible") emit(false);
      armInput();
    };
    const onVisibility = () => {
      clearTimeout(hiddenTimer);
      if (document.visibilityState === "hidden") hiddenTimer = setTimeout(() => emit(true), HIDDEN_IDLE_AFTER_MS);
      else onInput();
    };
    const onConnect = () => {
      const was = idle;
      idle = !was; // force a resend
      emit(was);
    };
    const events = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    const s = getSocket();
    s?.on("connect", onConnect);
    armInput();
    onVisibility();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onInput));
      document.removeEventListener("visibilitychange", onVisibility);
      s?.off("connect", onConnect);
      clearTimeout(inputTimer);
      clearTimeout(hiddenTimer);
    };
  }, []);

  useEffect(() => {
    const s = getSocket();
    const send = () => s?.emit("presence:focus", { communityId });
    send();
    s?.on("connect", send);
    return () => {
      s?.off("connect", send);
    };
  }, [communityId]);
}
